// Third Party
import * as React from 'react';
import Image from 'next/image';
import moment from 'moment';

// Utils
import {
  SearchRegionForListingsByPlaceNameWithSingleAssetQuery,
  useListingsListAllFavoritedQuery,
  ListingFavoriteSortOptions,
} from '@gql';
import { formatDateYYYYMMDD } from '@utils/date-helpers';
import { GRAY_PIXEL_DATA_URL } from '@utils/constants';
import { imagekitLoader } from '@utils/imagekit-loader';
import { routes } from '@routes';
import { useSession } from 'next-auth/react';

// UI
import { SearchPageContentFooter } from './search-page-content-footer';
import { SearchPageContentPartial } from '../seo-serp-page';
import { ListingCard } from '@components/listing-card';
import { AnalyticsRef } from '@components/analytics-ref';
import { SearchPageContentH1 } from './search-page-content-h1';
import serpImage from '../../../../public/assets/img/serp.jpg';
import { SearchForm } from '@components/search-form';
import { Typography } from '@components/typography';
import { MobileSearchModal } from '@components/mobile-search-modal';
import { MobileSearchModalProvider } from '@components/mobile-search-modal';
import { MobileSearchBar } from '@components/mobile-search-bar';
import { Link } from '@components/link';
import { ValueSection } from '../value-section';

type Props = {
  searchPageContent?: SearchPageContentPartial;
  regionDataEntries: SearchRegionForListingsByPlaceNameWithSingleAssetQuery['regionListingsCollection']['entries'];
};

export const SeoSearchList: React.FC<Props> = ({
  searchPageContent,
  regionDataEntries,
}) => {
  const { data: session } = useSession();
  const page = 1;
  const pageSize = 100;

  const { data: favoritedListings, refetch: refetchFavoritedListings } =
    useListingsListAllFavoritedQuery({
      variables: {
        page: page,
        pageSize: pageSize,
        sort: ListingFavoriteSortOptions.FavoritedAtNewestFirst,
      },
      skip: !session?.user,
    });

  return (
    <div className="relative space-y-4 lg:space-y-8">
      <div className="relative w-full md:text-center mt-4">
        <div className="relative z-10 mx-auto md:p-10">
          <div className="p-8 bg-gray-dark bg-opacity-50 rounded-lg max-w-screen-2xl mx-auto">
            <Typography
              component="h1"
              variant="title3"
              variantM="title2"
              className="mb-4 text-white text-center"
            >
              {searchPageContent && searchPageContent.h1
                ? searchPageContent.h1
                : ''}
            </Typography>

            <div className="mx-auto w-full z-40 hidden sm:block">
              <SearchForm
                placeId={
                  searchPageContent?.region?.placeId &&
                  searchPageContent?.region?.placeId
                }
                placeName={
                  searchPageContent?.region?.description &&
                  searchPageContent?.region?.description
                }
              />
            </div>

            <div className="mx-auto block sm:hidden">
              <MobileSearchModalProvider>
                <MobileSearchModal
                  onSubmit={(vals) => {
                    window.location.assign(
                      routes.searchShow({
                        placeId: vals.placeId,
                        checkInDate: vals.checkInDate?.format('YYYY-MM-DD'),
                        checkOutDate: vals.checkOutDate?.format('YYYY-MM-DD'),
                        adultCount: vals.adultCount,
                        childCount: vals.childCount,
                        infantCount: vals.infantCount,
                        petCount: vals.petCount,
                      })
                    );
                  }}
                  placeId={
                    (searchPageContent?.region?.placeId &&
                      searchPageContent?.region?.placeId) ||
                    undefined
                  }
                  placeName={
                    searchPageContent?.region?.description &&
                    searchPageContent?.region?.description
                  }
                />
                <MobileSearchBar
                  placeName={
                    (searchPageContent?.region?.description &&
                      searchPageContent?.region?.description) ||
                    'undefined'
                  }
                />
              </MobileSearchModalProvider>
            </div>
          </div>
        </div>
        <div className="absolute z-0 top-0 w-full h-full">
          {searchPageContent && searchPageContent.heroImageUrl ? (
            <Image
              src={searchPageContent.heroImageUrl}
              loader={imagekitLoader}
              alt={searchPageContent.h1}
              layout="fill"
              className="object-cover rounded-lg"
              placeholder="blur"
              blurDataURL={GRAY_PIXEL_DATA_URL}
              sizes="100vw"
            />
          ) : (
            <Image
              src={serpImage}
              alt={
                searchPageContent && searchPageContent.h1
                  ? searchPageContent.h1
                  : 'listing result'
              }
              layout="fill"
              className="object-cover rounded-lg"
              placeholder="blur"
              sizes="100vw"
            />
          )}
        </div>
      </div>

      <ValueSection />

      <SearchPageContentH1 searchPageContent={searchPageContent} />

      {/* SERP Listing Results */}
      <div className="flex flex-row relative">
        <div className="w-full">
          <div className="mt-4 sm:mt-0">
            <div className="md:block hidden">
              <div className="flex items-center">
                <Typography component="h2" variant="title3" className="mb-4">
                  Trending stays in {searchPageContent?.region?.description}
                </Typography>
              </div>
            </div>
            {regionDataEntries.length === 0 &&
              regionDataEntries.length === 0 && (
                <div className="grid mb-16 py-16">
                  <Typography
                    component="p"
                    variant="subtitle1"
                    className="mb-4 text-center"
                  >
                    We did not find any listings to satisfy your search.
                  </Typography>
                </div>
              )}

            {regionDataEntries.length > 0 && (
              <AnalyticsRef name="serp_search_results">
                <div className="grid grid-col-1 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 pb-4 gap-y-2 sm:gap-y-0 -mx-2 my-2 w-full">
                  {regionDataEntries.map((listing, index) => (
                    <div key={listing.slug}>
                      <ListingCard
                        listing={listing}
                        altTag={`${
                          searchPageContent?.region?.description || ''
                        }`}
                        accountShowRoute={routes.accounts(
                          listing.account.slug || ''
                        )}
                        avatarUrl={listing.account.avatarUrl}
                        displayStay={true}
                        showRoute={routes.listings({
                          slug: listing.slug,
                          checkInDate: listing.priceDisplay?.checkInDate
                            ? formatDateYYYYMMDD(
                                moment(listing.priceDisplay.checkInDate)
                              )
                            : undefined,
                          checkOutDate: listing.priceDisplay?.checkOutDate
                            ? formatDateYYYYMMDD(
                                moment(listing.priceDisplay.checkOutDate)
                              )
                            : undefined,
                        })}
                        // first two cards are LCP for mobile
                        priority={index < 2}
                        isFavorited={
                          favoritedListings?.listingsListAllFavorited
                            .__typename === 'ListingCollection' &&
                          favoritedListings.listingsListAllFavorited.entries.some(
                            (favoriteEntry) => favoriteEntry.id === listing.id
                          )
                        }
                        onFavoritedSuccess={() => {
                          refetchFavoritedListings();
                        }}
                      />
                    </div>
                  ))}
                </div>
              </AnalyticsRef>
            )}

            {searchPageContent?.region?.description && (
              <div className="flex justify-center w-full pt-4 pb-8 sm:pt-8 sm:pb-12">
                <Link
                  to={routes.searchShow({
                    placeId: searchPageContent?.region?.placeId,
                  })}
                  className="a flex py-2 px-4 items-center shadow-sm border-[0.025em] border-gray-main rounded-md space-x-2 p-2  justify-evenly"
                >
                  <div className="text-gray-dark text-sm font-medium">
                    Explore More
                  </div>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {searchPageContent && (
        <SearchPageContentFooter searchPageContent={searchPageContent} />
      )}

      <div className="block fixed right-1/2 bottom-0 -translate-y-8 translate-x-1/2 shadow-md sm:hidden">
        {searchPageContent?.region?.description && (
          <Link
            to={routes.searchShow({
              placeId: searchPageContent?.region?.placeId || '',
            })}
            className="a hidden xl:flex hover:text-gray-xlight px-2 py-4 items-center bg-gray-xlight shadow-sm border-[0.025em] border-gray-main rounded-full text-xs space-x-2 p-2 text-gray-dark justify-evenly"
          >
            <div className="text-gray-dark text-xs font-light">Show Map</div>
          </Link>
        )}
      </div>
    </div>
  );
};
